import { isLineVisibleForRole } from '@folio/hooks';
import { PriceBreakdown } from '@folio/modules/shared-quote/use-available-pricing';
import type { GetPriceOutput, PresetKey, PriceBreakdownLineOutputDto } from 'korint-sdk';
import type { PriceDisplayConfig } from 'korint-sdk/react';

export function resolvePresetAmount(price: GetPriceOutput, key: PresetKey): number {
  const [source, field] = key.split('.');
  const breakdown = price[source as keyof GetPriceOutput];
  if (!breakdown || typeof breakdown !== 'object') return 0;
  const value = (breakdown as Record<string, unknown>)[field];
  return typeof value === 'number' ? value : 0;
}

export function getVisibleLines(params: {
  lines: PriceBreakdownLineOutputDto[] | undefined;
  price: GetPriceOutput | undefined;
  priceBreakdownConfig: PriceDisplayConfig | undefined;
  firmType: string | undefined;
}): PriceBreakdownLineOutputDto[] {
  const { lines, price, priceBreakdownConfig, firmType } = params;
  const linesByKey = new Map<string, PriceBreakdownLineOutputDto[]>();
  for (const l of lines ?? []) {
    const group = linesByKey.get(l.key);
    if (group) group.push(l);
    else linesByKey.set(l.key, [l]);
  }
  const configuredLines = priceBreakdownConfig?.groups?.flatMap(g => g.lines);

  const isVisible = (l: PriceBreakdownLineOutputDto) => isLineVisibleForRole(l, firmType);

  if (configuredLines && configuredLines.length > 0) {
    return configuredLines
      .map((line): PriceBreakdownLineOutputDto | undefined => {
        if (line.type === 'PRESET') {
          if (!price) return undefined;
          const subLines = line.subLines?.flatMap(sub => {
            const subAmount = resolvePresetAmount(price, sub.key);
            return [{ key: sub.key, amount: subAmount, adjustedAmount: subAmount, forTypes: [] }];
          });
          const amount = resolvePresetAmount(price, line.key);
          return {
            key: line.key,
            amount,
            adjustedAmount: amount,
            forTypes: [],
            ...(subLines?.length && { subLines }),
          };
        }
        const candidates = linesByKey.get(line.key) ?? [];
        return candidates.find(c => isLineVisibleForRole(c, firmType)) ?? candidates[0];
      })
      .filter((l): l is PriceBreakdownLineOutputDto => !!l && isVisible(l));
  }

  return (lines ?? []).filter(l => isVisible(l));
}

export function getDefaultLines(price: {
  recurring?: { withFeesAndTaxes: number; taxes: number };
}): PriceBreakdownLineOutputDto[] {
  const preTaxes = (price.recurring?.withFeesAndTaxes ?? 0) - (price.recurring?.taxes ?? 0);
  const taxes = price.recurring?.taxes ?? 0;
  return [
    {
      key: 'PRE_TAXES',
      amount: preTaxes,
      adjustedAmount: preTaxes,
      forTypes: [],
    },
    {
      key: 'TAXES',
      amount: taxes,
      adjustedAmount: taxes,
      forTypes: [],
    },
  ];
}

export function getTotals(price: PriceBreakdown, priceBreakdownConfig?: PriceDisplayConfig) {
  if (priceBreakdownConfig?.totals?.length) {
    return priceBreakdownConfig.totals.map(total => ({
      key: total.key,
      amount: resolvePresetAmount(price, total.key),
    }));
  }
  return [{ key: 'TOTAL', amount: price.recurring?.withFeesAndTaxes ?? 0 }];
}

export function getVariantByKey(
  priceBreakdownConfig: PriceDisplayConfig | undefined,
): Map<string, 'bold' | 'dimmed' | undefined> {
  const variantByKey = new Map<string, 'bold' | 'dimmed' | undefined>();
  if (priceBreakdownConfig?.groups) {
    for (const group of priceBreakdownConfig.groups) {
      for (const line of group.lines) {
        variantByKey.set(line.key as string, line.variant);
      }
    }
  }
  return variantByKey;
}

export function getTooltipKeys(priceBreakdownConfig: PriceDisplayConfig | undefined): Set<string> {
  const keys = new Set<string>();
  if (priceBreakdownConfig?.groups) {
    for (const group of priceBreakdownConfig.groups) {
      for (const line of group.lines) {
        if (line.tooltip) keys.add(line.key as string);
      }
    }
  }
  return keys;
}

/**
 * Creates a translation key fallback chain for quote line labels.
 * Tries product-specific translations first, then falls back to generic ones.
 */
export function getQuoteLineTranslationKeys(productId: string, key: string): string[] {
  return [`${productId}.quoteLines.${key}`, `PriceBreakdown.${key}`];
}
